/**
 * Live HTTP implementation of the B1.8 Payment feature API.
 *
 * Targets the Phase B1.8 / ADR-109 backend surface at
 * `/api/biz/cashier/payments/*` (see
 * `backend/src/api/business/biz-payment-routes.ts`):
 *
 *   POST  /biz/cashier/payments/intents       — initiateCardPayment
 *   POST  /biz/cashier/payments/3ds-callback  — handle3DSCallback
 *   GET   /biz/cashier/payments/:id           — polling snapshot
 *   POST  /biz/cashier/payments/:id/refund    — refundCardPayment (manager+)
 *
 * Contract alignment:
 *   The BE returns a thin `InitiateCardPaymentResult` /
 *   `Handle3DSCallbackResult` shape:
 *     { paymentIntentId, status, threeDsHtml?, providerPaymentId?,
 *       errorCode?, errorMessage? }
 *   The FE `PaymentIntentSnapshot` is a richer view (adds amountMinor /
 *   currency / reference / createdAt) so the Cashier feature can render
 *   tender previews without a second round-trip. We project the BE row by
 *   merging the request-side fields (the FE owns these) with the
 *   server-side fields (status, ids, error envelope).
 *
 *   `paymentId` (FE-internal id) := `paymentIntentId` (BE UUID) in live
 *   mode. This is intentional — the React Query keys, inflight map keys,
 *   and `useConfirm3DS` lookups all use `paymentId` as the stable handle
 *   regardless of mode. The BE UUID just happens to be the canonical id.
 *
 * Card capture model:
 *   Iyzico's TR 3DS flow uses an issuer-bank hosted form rendered inside
 *   `threeDsHtml`. Card PAN/CVV are NEVER sent to PeToTa BE — the FE card
 *   form is a UX shell that hands off to the iframe. The `card.cardHolder`
 *   field is forwarded to `customerName` only as a fallback when the
 *   caller did not pass an explicit `customerEmail`/`customerName`.
 *
 * Customer metadata (Iyzico KYC compliance):
 *   `customerEmail` + `customerName` are required by Iyzico. When the
 *   caller omits them (walk-in customer), we synthesize safe placeholders
 *   keyed off the order id so the gateway round-trip stays valid without
 *   leaking PII into invoice/receipt surfaces. These placeholders live
 *   only in `payment_intents.metadata_json`.
 *
 * Refunds:
 *   `refundCardPayment` is exported here for completeness but the
 *   PaymentDialog does NOT call it — refund UX lives in the Invoice
 *   feature (`InvoiceDetailPage` → ADR-110 DayClose). Exposing the helper
 *   keeps the live surface symmetric with the BE plugin.
 *
 * @see payment-api-mock.ts (deterministic in-memory provider)
 * @see payment-api.ts (mock↔live dispatcher)
 * @see backend/src/api/business/biz-payment-routes.ts
 * @see backend/src/domains/business/biz-payment-service.ts
 * @see docs/decisions/business/ADR-109-business-payment-tender-engine.md
 */

import { ApiError } from '@/shared/api/apiClient';
import { bizGet, bizPost } from '@/shared/api/bizClient';

import type {
  Confirm3DSRequest,
  PaymentFlowStatus,
  PaymentIntentSnapshot,
  StartCardPaymentRequest,
  StartGiftCardPaymentRequest,
  StartTransferPaymentRequest,
} from './types';
import { last4, normalizeCardNumber } from './payment-helpers';

// ---------------------------------------------------------------------------
// BE response shapes (mirrors `biz-payment-service.ts` exports)
// ---------------------------------------------------------------------------

type BeIntentStatus = 'pending_3ds' | 'succeeded' | 'failed';

interface BeInitiateResult {
  readonly paymentIntentId: string;
  readonly status: BeIntentStatus;
  readonly threeDsHtml: string | null;
  readonly providerPaymentId: string | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
}

interface BeHandle3DSResult {
  readonly paymentIntentId: string;
  readonly status: 'succeeded' | 'failed';
  readonly providerPaymentId: string | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
}

interface BeRefundResult {
  readonly paymentIntentId: string;
  readonly status: 'succeeded' | 'partially_refunded' | 'refunded' | 'failed';
  readonly refundedAmountMinor: number;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
}

interface BeCashierPaymentSummary {
  readonly paymentIntentId: string;
  readonly status: BeIntentStatus;
  readonly amountMinor: number;
  readonly currency: 'TRY' | 'USD' | 'EUR';
  readonly providerPaymentId: string | null;
  readonly threeDsHtml: string | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  readonly createdAt: string;
  readonly updatedAt: string | null;
  readonly paidAt: string | null;
}

// ---------------------------------------------------------------------------
// Live → FE projection
// ---------------------------------------------------------------------------

function projectInitiate(
  be: BeInitiateResult,
  req: StartCardPaymentRequest,
): PaymentIntentSnapshot {
  const digits = normalizeCardNumber(req.card.cardNumber);
  return {
    paymentId: be.paymentIntentId,
    paymentIntentId: be.paymentIntentId,
    providerPaymentId: be.providerPaymentId,
    provider: 'iyzico',
    status: be.status as PaymentFlowStatus,
    method: 'card',
    amountMinor: req.amountMinor,
    currency: req.currency,
    reference: digits.length > 0 ? last4(digits) : null,
    threeDsHtml: be.threeDsHtml,
    errorCode: be.errorCode,
    errorMessage: be.errorMessage,
    createdAt: new Date().toISOString(),
  };
}

function projectCallback(
  be: BeHandle3DSResult,
  base: { amountMinor: number; currency: string; reference: string | null; createdAt: string },
): PaymentIntentSnapshot {
  return {
    paymentId: be.paymentIntentId,
    paymentIntentId: be.paymentIntentId,
    providerPaymentId: be.providerPaymentId,
    provider: 'iyzico',
    status: be.status as PaymentFlowStatus,
    method: 'card',
    amountMinor: base.amountMinor,
    currency: base.currency,
    reference: base.reference,
    threeDsHtml: null,
    errorCode: be.errorCode,
    errorMessage: be.errorMessage,
    createdAt: base.createdAt,
  };
}

function projectSummary(be: BeCashierPaymentSummary): PaymentIntentSnapshot {
  return {
    paymentId: be.paymentIntentId,
    paymentIntentId: be.paymentIntentId,
    providerPaymentId: be.providerPaymentId,
    provider: 'iyzico',
    status: be.status as PaymentFlowStatus,
    method: 'card',
    amountMinor: be.amountMinor,
    currency: be.currency,
    reference: null,
    threeDsHtml: be.threeDsHtml,
    errorCode: be.errorCode,
    errorMessage: be.errorMessage,
    createdAt: be.createdAt,
  };
}

// ---------------------------------------------------------------------------
// Inflight cache — preserves request-side fields across the 3DS round-trip
// ---------------------------------------------------------------------------

interface InflightContext {
  readonly amountMinor: number;
  readonly currency: string;
  readonly reference: string | null;
  readonly createdAt: string;
}

const inflightCtx = new Map<string, InflightContext>();

// ---------------------------------------------------------------------------
// Customer metadata fallbacks (Iyzico KYC compliance)
// ---------------------------------------------------------------------------

function safeCustomerEmail(req: StartCardPaymentRequest): string {
  if (req.customerEmail && req.customerEmail.trim().length >= 3) {
    return req.customerEmail.trim();
  }
  // Walk-in placeholder — never surfaces on receipts/invoices, lives only in
  // `payment_intents.metadata_json` for Iyzico KYC compliance.
  return `walkin+${req.orderId}@business.petota.local`;
}

function safeCustomerName(req: StartCardPaymentRequest): string {
  if (req.customerName && req.customerName.trim().length >= 2) {
    return req.customerName.trim();
  }
  if (req.card.cardHolder && req.card.cardHolder.trim().length >= 2) {
    return req.card.cardHolder.trim();
  }
  return 'Walk-in Customer';
}

function safeCallbackUrl(req: StartCardPaymentRequest): string {
  if (req.callbackUrl && req.callbackUrl.trim().length >= 8) {
    return req.callbackUrl.trim();
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/business/payment/3ds-callback`;
  }
  const configured = (import.meta.env['VITE_BUSINESS_WEB_ORIGIN'] as string | undefined)?.trim();
  const base = configured && configured.length > 0 ? configured.replace(/\/$/, '') : 'https://business.petota.com';
  return `${base}/payment/3ds-callback`;
}

// ---------------------------------------------------------------------------
// Card payment (live)
// ---------------------------------------------------------------------------

export async function startCardPayment(
  req: StartCardPaymentRequest,
): Promise<PaymentIntentSnapshot> {
  try {
    const be = await bizPost<BeInitiateResult>(
      '/biz/cashier/payments/intents',
      {
        orderId: req.orderId,
        amountMinor: req.amountMinor,
        currency: req.currency as 'TRY' | 'USD' | 'EUR',
        customerEmail: safeCustomerEmail(req),
        customerName: safeCustomerName(req),
        customerPhone: req.customerPhone?.trim() || undefined,
        callbackUrl: safeCallbackUrl(req),
        idempotencyKey: req.idempotencyKey,
      },
    );

    const snap = projectInitiate(be, req);
    inflightCtx.set(snap.paymentId, {
      amountMinor: snap.amountMinor,
      currency: snap.currency,
      reference: snap.reference,
      createdAt: snap.createdAt,
    });
    return snap;
  } catch (err) {
    return failedSnapshot(req, err);
  }
}

export async function confirm3DS(
  req: Confirm3DSRequest,
): Promise<PaymentIntentSnapshot> {
  const intentId = req.paymentIntentId ?? req.paymentId;
  const ctx = inflightCtx.get(intentId) ?? {
    amountMinor: 0,
    currency: 'TRY',
    reference: null,
    createdAt: new Date().toISOString(),
  };

  // Timeout / cancel path — the FE 3DS overlay drives `onResult('failure')`
  // when the iframe never posts a conversation token (3-minute timeout, user
  // dismisses, postMessage blocked by sandbox). Don't hit the BE in that
  // case; synthesize a failed snapshot so the dialog re-enables retry.
  if (!intentId || !req.conversationToken) {
    return {
      paymentId: intentId || `pay_lost_${Date.now()}`,
      paymentIntentId: req.paymentIntentId ?? null,
      providerPaymentId: null,
      provider: 'iyzico',
      status: 'failed',
      method: 'card',
      amountMinor: ctx.amountMinor,
      currency: ctx.currency,
      reference: ctx.reference,
      threeDsHtml: null,
      errorCode: '3ds.timeout_or_cancelled',
      errorMessage: '3D Secure doğrulaması tamamlanamadı.',
      createdAt: ctx.createdAt,
    };
  }

  try {
    const be = await bizPost<BeHandle3DSResult>(
      '/biz/cashier/payments/3ds-callback',
      {
        paymentIntentId: intentId,
        conversationToken: req.conversationToken,
      },
    );
    return projectCallback(be, ctx);
  } catch (err) {
    return {
      paymentId: intentId,
      paymentIntentId: intentId,
      providerPaymentId: null,
      provider: 'iyzico',
      status: 'failed',
      method: 'card',
      amountMinor: ctx.amountMinor,
      currency: ctx.currency,
      reference: ctx.reference,
      threeDsHtml: null,
      errorCode: extractErrorCode(err) ?? '3ds.failed',
      errorMessage: extractErrorMessage(err) ?? '3D Secure doğrulaması başarısız.',
      createdAt: ctx.createdAt,
    };
  }
}

/**
 * Polls a payment intent — used by the iframe modal when postMessage is
 * unreliable (e.g. cross-origin iframe blocked the script). Live-only;
 * mock callers should use `confirm3DS` with `threeDsResult` instead.
 */
export async function getPaymentSnapshot(
  paymentIntentId: string,
): Promise<PaymentIntentSnapshot> {
  const be = await bizGet<BeCashierPaymentSummary>(
    `/biz/cashier/payments/${paymentIntentId}`,
  );
  return projectSummary(be);
}

// ---------------------------------------------------------------------------
// Refund (live, manager+ only — invoked from Invoice feature, not PaymentDialog)
// ---------------------------------------------------------------------------

export interface RefundCardPaymentRequest {
  readonly paymentIntentId: string;
  readonly amountMinor: number;
  /** ≥20 chars (INV-14). */
  readonly reason: string;
  readonly idempotencyKey: string;
}

export async function refundCardPayment(
  req: RefundCardPaymentRequest,
): Promise<BeRefundResult> {
  return bizPost<BeRefundResult>(
    `/biz/cashier/payments/${req.paymentIntentId}/refund`,
    {
      amountMinor: req.amountMinor,
      reason: req.reason,
      idempotencyKey: req.idempotencyKey,
    },
  );
}

// ---------------------------------------------------------------------------
// Gift card / transfer — no live BE counterpart yet (deferred to Faz 1.4).
// We synthesize a "succeeded" snapshot client-side so the cashier flow stays
// usable; the resulting tender will NOT carry a `paymentIntentId` and the
// commit step persists it as a regular non-gateway tender.
// ---------------------------------------------------------------------------

export async function startGiftCardPayment(
  req: StartGiftCardPaymentRequest,
): Promise<PaymentIntentSnapshot> {
  const trimmed = req.code.trim().toUpperCase();
  const valid = /^GC-[A-Z0-9]{6,10}$/.test(trimmed);
  const now = new Date().toISOString();
  return {
    paymentId: `pay_gc_${Date.now()}`,
    paymentIntentId: null,
    providerPaymentId: null,
    provider: 'iyzico',
    status: valid ? 'succeeded' : 'failed',
    method: 'gift_card',
    amountMinor: req.amountMinor,
    currency: req.currency,
    reference: trimmed,
    threeDsHtml: null,
    errorCode: valid ? null : 'gift_card.invalid',
    errorMessage: valid ? null : 'Hediye kartı kodu geçersiz.',
    createdAt: now,
  };
}

export async function startTransferPayment(
  req: StartTransferPaymentRequest,
): Promise<PaymentIntentSnapshot> {
  const ok = req.reference.trim().length >= 4;
  const now = new Date().toISOString();
  return {
    paymentId: `pay_tr_${Date.now()}`,
    paymentIntentId: null,
    providerPaymentId: null,
    provider: 'iyzico',
    status: ok ? 'succeeded' : 'failed',
    method: 'transfer',
    amountMinor: req.amountMinor,
    currency: req.currency,
    reference: req.reference.trim(),
    threeDsHtml: null,
    errorCode: ok ? null : 'transfer.reference_missing',
    errorMessage: ok ? null : 'Havale referansı zorunlu.',
    createdAt: now,
  };
}

// ---------------------------------------------------------------------------
// Error envelope helpers
// ---------------------------------------------------------------------------

function extractErrorCode(err: unknown): string | null {
  if (err instanceof ApiError) {
    // BE biz-payment-routes returns `{ error, message, providerErrorCode? }`
    // (NOT RFC 7807). Axios captures it verbatim into `payload` even though
    // the type is `ApiErrorShape` — read it as the loose shape it actually
    // is at runtime.
    const body = err.payload as unknown as
      | { error?: unknown; type?: unknown }
      | null
      | undefined;
    if (body && typeof body.error === 'string') {
      return body.error;
    }
    if (body && typeof body.type === 'string' && body.type !== 'about:blank') {
      return body.type;
    }
  }
  return null;
}

function extractErrorMessage(err: unknown): string | null {
  if (err instanceof ApiError) {
    const body = err.payload as unknown as
      | { message?: unknown; error?: unknown; detail?: unknown; title?: unknown }
      | null
      | undefined;
    if (body && typeof body.message === 'string') {
      return body.message;
    }
    if (body && typeof body.detail === 'string') {
      return body.detail;
    }
    if (body && typeof body.title === 'string') {
      return body.title;
    }
    if (body && typeof body.error === 'string') {
      return body.error;
    }
    return err.message ?? null;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return null;
}

function failedSnapshot(
  req: StartCardPaymentRequest,
  err: unknown,
): PaymentIntentSnapshot {
  const digits = normalizeCardNumber(req.card.cardNumber);
  return {
    paymentId: `pay_failed_${Date.now()}`,
    paymentIntentId: null,
    providerPaymentId: null,
    provider: 'iyzico',
    status: 'failed',
    method: 'card',
    amountMinor: req.amountMinor,
    currency: req.currency,
    reference: digits.length > 0 ? last4(digits) : null,
    threeDsHtml: null,
    errorCode: extractErrorCode(err) ?? 'card.gateway_error',
    errorMessage: extractErrorMessage(err) ?? 'Ödeme başlatılamadı.',
    createdAt: new Date().toISOString(),
  };
}
